<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('product.products') }}
        </span>
        <h3 class="page-title">
          <template v-if="isIndentList">
            {{ $t('product.addingNewIndentProduct') }}
          </template>
          <template v-else>
            {{ $t('product.addingNewProduct') }}
          </template>
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push(backUrl)"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('product.addProduct') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <validation-observer ref="product-new-form" tag="form">
        <d-card class="mb-5">
          <d-tabs card v-if="!loadError">
            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('ui.generalInfo') }}
              </h4>

              <div class="form-group">
                <label>
                  {{ $t('product.productName') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <d-row>
                <d-col cols="4">
                  <div class="form-group">
                    <label>
                      {{ $t('product.minimalQty') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="minimum_quantity"
                      v-slot="{ errors }">
                      <d-input-group :append="$t('ui.piecesAbbr')">
                        <d-input
                          type="number"
                          :min="0"
                          :step="1"
                          :state="errors.length ? 'invalid' : null"
                          v-model="minimum_quantity" />
                      </d-input-group>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div class="form-group">
                    <label>
                       {{ $t('product.piecesInBox') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="pieces_in_carton"
                      v-slot="{ errors }">
                      <d-input-group :append="$t('ui.piecesAbbr')">
                        <d-input
                          type="number"
                          :min="1"
                          :step="1"
                          :state="errors.length ? 'invalid' : null"
                          v-model="pieces_in_carton" />
                      </d-input-group>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div class="form-group">
                    <label>
                      {{ $t('product.actualPiecesInBox') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="real_pieces_in_carton"
                      v-slot="{ errors }">
                      <d-input-group :append="$t('ui.piecesAbbr')">
                        <d-input
                          type="number"
                          :min="1"
                          :step="1"
                          :state="errors.length ? 'invalid' : null"
                          v-model="real_pieces_in_carton" />
                      </d-input-group>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.productCode') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.prductPriceCode') }}:
                    </label>

                    <validation-provider
                      :rules="isIndentList ? '' : 'required'"
                      name="price_code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="price_code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="2">
                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="include_discounts"
                      toggle
                      toggle-small>
                      {{ $t('product.includeDiscounts') }}
                    </d-checkbox>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="include_in_services"
                      toggle
                      toggle-small>
                      {{ $t('product.includeInServices') }}
                    </d-checkbox>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="disable_for_customer"
                      toggle
                      toggle-small>
                      {{ $t('product.disableForCustomer') }}
                    </d-checkbox>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="disable_for_customer_api"
                      toggle
                      toggle-small>
                      {{ $t('product.disableForCustomerAPI') }}
                    </d-checkbox>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="auto_sample_date"
                      toggle
                      toggle-small>
                      {{ $t('product.automaticallySetSamplesDates') }}
                    </d-checkbox>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="available_in_purchase"
                      toggle
                      toggle-small>
                      {{ $t('product.availableInPurchases') }}
                    </d-checkbox>
                  </div>
                </d-col>
              </d-row>

              <d-row v-if="available_in_purchase && !isIndentList">
                <d-col cols="6">
                  <purchase-select-shipper
                    key="purchase-shipper-field"
                    ref="purchase-shipper"
                    :isSub="false" />
                </d-col>

                <d-col cols="6">
                  <purchase-select-shipper
                    key="purchase-subshipper-field"
                    ref="purchase-subshipper"
                    :isSub="true" />
                </d-col>
              </d-row>

              <d-row
                v-if="available_in_purchase && !isIndentList"
                class="mb-2 mt-1">
                <d-col cols="4">
                  <strong>
                    {{ $t('product.properties.material') }}:
                  </strong>
                  <br>
                  {{ getMaterialNameByColor(color_id) }}
                </d-col>
                <d-col cols="4">
                  <strong>
                    {{ $t('product.properties.color') }}:
                  </strong>
                  <br>
                  <div :class="{ 'form-control': true, 'is-select': true }">
                    <v-select
                      :options="getColorsForUse"
                      v-model="color_id"
                      :placeholder="$t('purchase.fabric.chooseColor')"
                      label="label"
                      :reduce="color => color.value">
                      <template slot="no-options">
                        {{ $t('purchase.fabric.noSuchColor') }}&hellip;
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.text }}
                        </div>
                      </template>
                      <template
                        slot="selected-option"
                        slot-scope="option">
                        <div
                          :key="'color-id-' + option.value"
                          class="selected d-center">
                          {{ option.text }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                </d-col>
                <d-col cols="4">
                  <strong>
                    {{ $t('product.properties.strand') }}:
                  </strong>
                  <br>
                  <div :class="{ 'form-control': true, 'is-select': true }">
                    <v-select
                      :options="getStrands"
                      v-model="strand_id"
                      :placeholder="$t('product.properties.chooseStrand')"
                      label="text"
                      :reduce="color => color.value">
                      <template slot="no-options">
                        {{ $t('product.properties.noSuchStrand') }}&hellip;
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.text }}
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.text }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                </d-col>
              </d-row>

              <d-row
                v-if="available_in_purchase && !isIndentList"
                class="mb-2 mt-1">
                <d-col cols="12">
                  <strong>
                    {{ $t('product.properties.sizes') }}:
                  </strong>
                  <br>
                  <div :class="{ 'form-control': true, 'is-select': true }">
                    <v-select
                      :options="getSizes"
                      v-model="sizes"
                      :placeholder="$t('purchase.size.selectSize')"
                      :reduce="size => size.value"
                      label="text"
                      :multiple="true">
                      <template slot="no-options">
                        {{ $t('purchase.size.noSuchSize') }}&hellip;
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.text }}
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.text }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                </d-col>
              </d-row>

              <d-row
                v-if="available_in_purchase && !isIndentList"
                class="mb-4 mt-1">
                <d-col cols="12">
                  <strong>
                    {{ $t('product.properties.procedures') }}:
                  </strong>
                  <br>
                  <div :class="{ 'form-control': true, 'is-select': true }">
                    <v-select
                      :options="getProcedures"
                      v-model="procedures"
                      :placeholder="$t('product.properties.chooseProcedure')"
                      :reduce="procedure => procedure.value"
                      label="text"
                      :multiple="true">
                      <template slot="no-options">
                        {{ $t('product.properties.noSuchProcedure') }}&hellip;
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.text }}
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.text }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col
                  v-if="isIndentList"
                  cols="6">
                  <label>
                    {{ $t('product.productIndentBaseProduct') }}:
                  </label>

                  <div class="form-group">
                    <div :class="{ 'form-control': true, 'is-select': true }">
                      <v-select
                        class="w-100"
                        :options="availableProducts"
                        v-model="base_product_id"
                        :reduce="product => product.id"
                        :placeholder="$t('product.chooseProduct')"
                        label="code"
                        :filterBy="filterByForProducts">
                        <template slot="no-options">
                          {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
                        </template>
                        <template slot="option" slot-scope="option">
                          <div class="d-center">
                            <template v-if="option.code">{{ option.code}} - </template>{{ option.name }}
                          </div>
                        </template>
                        <template slot="selected-option" slot-scope="option">
                          <div class="selected d-center">
                            <template v-if="option.code">{{ option.code}} - </template>{{ option.name }}
                          </div>
                        </template>
                      </v-select>
                    </div>
                  </div>
                </d-col>

                <d-col :cols="isIndentList ? 6 : 12">
                  <label>
                    {{ $t('product.productDefaultServices') }}:
                  </label>

                  <div class="form-group">
                    <div :class="{ 'form-control': true, 'is-select': true }">
                      <v-select
                        class="w-100"
                        :options="availableServices"
                        v-model="default_services"
                        :reduce="service => service.id"
                        :placeholder="$t('service.chooseService')"
                        label="code"
                        :filterBy="filterByForServices"
                        :multiple="true">
                        <template slot="no-options">
                          {{ $t('service.noServicesMatchingPhrase') }}&hellip;
                        </template>
                        <template slot="option" slot-scope="option">
                          <div class="d-center">
                            <template v-if="option.group_name">{{ option.group_name }} - </template>{{ option.name }}
                          </div>
                        </template>
                        <template slot="selected-option" slot-scope="option">
                          <div class="selected d-center">
                            <template v-if="option.group_name">{{ option.group_name }} - </template>{{ option.name }}
                          </div>
                        </template>
                      </v-select>
                    </div>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <template>
                  <d-col cols="3">
                    <div class="form-group">
                      <label>
                        {{ $t('product.indentProduct.weight') }}:
                      </label>

                      <validation-provider
                        rules="required|min_value:0|doubleVal"
                        name="weight"
                        v-slot="{ errors }">
                        <d-input-group :class="{'is-invalid': errors.length}">
                          <d-input
                            type="text"
                            class="text-right"
                            :state="errors.length ? 'invalid' : null"
                            v-model="weight" />
                          <d-input-group-text slot="append">
                            kg
                          </d-input-group-text>
                        </d-input-group>
                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </div>
                  </d-col>
                </template>

                <d-col cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('product.indentProduct.cnCode') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="cn_code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="cn_code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col :cols="isIndentList ? 6 : 9">
                  <div class="form-group">
                    <label>
                      {{ $t('product.linkToWWWPage') }}:
                    </label>

                    <validation-provider
                      rules="valid-url"
                      name="site-url"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="site_url" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="4">
                  <label class="d-block">
                      {{ $t('product.indentProduct.type') }}:
                  </label>

                  <d-button-group size="small" class="">
                    <d-button
                      theme="primary"
                      :outline="type !== null"
                      @click.prevent="setIndentProductType(null)">
                      {{ $t('product.indentProduct.types.none') }}
                    </d-button>
                    <d-button
                      theme="primary"
                      :outline="type !== 'CAP'"
                      @click.prevent="setIndentProductType('CAP')">
                      {{ $t('product.indentProduct.types.cap') }}
                    </d-button>
                    <d-button
                      theme="primary"
                      :outline="type !== 'WINTER'"
                      @click.prevent="setIndentProductType('WINTER')">
                      {{ $t('product.indentProduct.types.winter') }}
                    </d-button>
                  </d-button-group>
                </d-col>

                <d-col
                  v-if="isIndentList"
                  cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('product.indentProduct.originCountry') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="origin_country"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="origin_country" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col :cols="isIndentList ? 6 : 8">
                  <div class="form-group">
                    <label>
                      {{ $t('product.linkToDropbox') }}:
                    </label>

                    <validation-provider
                      rules="valid-url"
                      name="dropbox-url"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="dropbox_url" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <div class="form-group">
                <label>
                  {{ $t('ui.shortDescription') }}:
                </label>

                <validation-provider
                  :rules="isIndentList ? '' : 'required'"
                  name="intro"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="intro" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('product.productDescription') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="description"
                  v-slot="{ errors }">
                  <d-textarea
                    :state="errors.length ? 'invalid' : null"
                    v-model="description"
                    :rows="8" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <hr />

              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.siteDescription') }}:
                    </label>

                    <d-textarea
                      v-model="site_description"
                      :rows="8" />
                  </div>
                </d-col>

                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.descriptionForCustomer') }}:
                    </label>

                    <d-textarea
                      v-model="description_for_customer"
                      :rows="8" />
                  </div>
                </d-col>
              </d-row>

              <hr />

              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.productPhoto') }}:
                    </label>

                    <file-input
                      :onChange="setProductImage"
                      id="inputFileProductImage" />
                  </div>
                </d-col>

                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.visualizationTemplate') }}:
                    </label>

                    <file-input
                      :onChange="setVisualizationTemplate"
                      id="inputFileVisualizationTemplateImage" />
                  </div>
                </d-col>
              </d-row>
            </d-tab>

            <d-tab
              v-if="!isIndentList"
              :title="$t('image.photoGallery')">
              <p class="text-center">
                {{ $t('product.addingMorePhotosAvailableAfterProductSave') }}
              </p>
            </d-tab>

            <d-tab
              v-if="!isIndentList"
              :title="$t('image.webGallery')">
              <p class="text-center">
                {{ $t('product.addingMorePhotosAvailableAfterProductSave') }}
              </p>
            </d-tab>

            <d-tab :title="$t('price.prices')">
              <div class="form-group">
                <label>
                  {{ $t('product.importantPriceInfo') }}:
                </label>

                <d-textarea
                  v-model="price_message"
                  :rows="4" />
              </div>

              <p
                v-if="!isIndentList"
                class="text-center">
                {{ $t('product.pricesListWillBeAvailableAfterProductSave') }}
              </p>

              <h4
                v-if="isIndentList"
                class="mb-3 pb-3 border-bottom d-flex">
                {{ $t('product.productPrices') }}

                <d-button
                  theme="success"
                  @click.prevent="addPrice"
                  class="ml-auto">
                  <i class="material-icons">add</i>
                  {{ $t('price.addPrice') }}
                </d-button>
              </h4>

              <table
                v-if="isIndentList"
                class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0">{{ $t('price.price') }}</th>
                    <th scope="col" class="border-0">{{ $t('settings.currency.currency') }}</th>
                    <th scope="col" class="border-0" style="width: 100px">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="prices.length">
                  <tr
                    v-for="(price, index) of prices"
                    :key="'price-' + index">
                    <td class="align-middle">
                      <validation-provider
                        rules="required|min_value:0|doubleVal"
                        :name="'product-indent-price-' + index"
                        v-slot="{ errors }">
                        <d-input
                          v-model="price.price"
                          :state="errors.length ? 'invalid' : null" />
                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </td>
                    <td>
                      <validation-provider
                        rules="required"
                        :name="'product-indent-currency-' + index"
                        class="w-25 ml-2"
                        v-slot="{ errors }">
                        <d-form-select
                          v-model="price.currency_id"
                          :state="errors.length ? 'invalid' : null"
                          :options="currencyOptions" />
                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </td>
                    <td>
                      <d-button
                        theme="danger"
                        @click.native.prevent="removePrice(index)"
                        :title="$t('ui.delete')">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="3" class="text-center">
                      {{ $t('product.noPricesDefinedForThisProduct') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-tab>

            <d-tab
              :title="$t('settings.transaltion.translations')">
              <p class="text-center">
                {{ $t('product.addingTranslationsAvailableAfterProductSave') }}
              </p>
            </d-tab>

            <d-tab :title="$t('ui.tags')">
              <p class="text-center">
                {{ $t('product.tag.addingTagsAvailableAfterProductSave') }}
              </p>
            </d-tab>

            <d-tab :title="$t('productCategories.categories')">
              <div
                v-if="allDataLoaded"
                class="form-group">
                <label>
                  {{ $t('productCategories.mainCategory') }}:
                </label>

                <div :class="{
                  'form-control': true,
                  'is-select': true,
                }">
                  <v-select
                    :options="categoriesStructure"
                    v-model="category_id"
                    :reduce="category => category.id"
                    :placeholder="$t('productCategories.noMainCategory')"
                    label="name"
                    :clearable="true">
                    <template slot="no-options">
                      {{ $t('productCategories.noCategoriesToDisplay') }}&hellip;
                    </template>
                    <template slot="option" slot-scope="option">
                      <div class="d-center" v-pure-html="option.name"></div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                      <div class="selected d-center" v-pure-html="option.name"></div>
                    </template>
                  </v-select>
                </div>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('productCategories.additionalCategories') }}:
                </label>

                <ul class="additional-categories">
                  <li
                    v-for="(category, index) of categoriesStructure"
                    :key="'additional-category-' + index"
                    :class="{
                      'additional-category': true,
                      'is-disabled': category.id === category_id
                    }">
                    <d-checkbox
                      v-model="additionalCategories[category.id]"
                      toggle
                      toggle-small
                      :disabled="category.id === category_id">
                      <span v-pure-html="category.name"></span>
                    </d-checkbox>
                  </li>
                </ul>
              </div>
            </d-tab>

            <d-tab :title="$t('product.stateHistory.tabTitle')">
              <p class="text-center">
                {{ $t('product.stateHistory.availableAfterProductSave') }}
              </p>
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>
  </d-container>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';
import PurchaseSelectShipper from './../purchases/PurchaseSelectShipper.vue';

export default {
  name: 'product-new',
  components: {
    'purchase-select-shipper': PurchaseSelectShipper
  },
  computed: {
    allDataLoaded () {
      return this.loadedServices && this.loadedProducts && this.loadedCategories && this.loadedCurrencies;
    },
    backUrl () {
      if (this.isIndentList) {
        return '/produkty-indent';
      }

      return '/produkty';
    },
    isIndentList () {
      return this.$route.path.indexOf('/produkty-indent') > -1;
    },
    currencyOptions () {
      return [{
        text: this.$t('shipments.popups.selectCurrency'),
        value: null
      }].concat(CommonGetters.getCurrenciesForSelect(this.currencies));
    },
    categoriesStructure () {
      let result = [];
      let stack = this.categoriesList.map(category => ({ ...category, prefix: '' }));

      while (stack.length > 0) {
        let current = stack.shift();

        result.push({
          id: current.id,
          name: current.prefix + current.name
        });

        if (current.children && current.children.length > 0) {
          stack = [
            ...current.children.map(child => ({ ...child, prefix: current.prefix + '<span style="color: #aaa;">&ndash;</span> ' })),
            ...stack
          ];
        }
      }

      return result;
    },
    getStrands () {
      if (this.productProperties && this.productProperties.strands) {
        return this.productProperties.strands.map(strand => ({
          text: strand.name,
          value: strand.id
        }));
      }

      return [];
    },
    getSizes () {
      if (this.productProperties && this.productProperties.sizes) {
        return this.productProperties.sizes.map(size => ({
          text: size.name,
          value: size.id
        }));
      }

      return [];
    },
    getProcedures () {
      if (this.productProperties && this.productProperties.procedures) {
        return this.productProperties.procedures.map(procedure => ({
          text: procedure.name,
          value: procedure.id
        }));
      }

      return [];
    },
    getColors () {
      if (this.productProperties && this.productProperties.materials) {
        let allColors = [];
        let addedColors = [];
        let colors = this.productProperties.materials.map(material => material.colors);

        for (let i = 0; i < colors.length; i++) {
          for (let j = 0; j < colors[i].length; j++) {
            if (addedColors.indexOf(colors[i][j].id) === -1) {
              allColors.push({
                text: colors[i][j].name,
                value: colors[i][j].id
              });

              addedColors.push(colors[i][j].id);
            }
          }
        }

        return allColors;
      }

      return [];
    },
    getColorsForUse () {
      return this.getColors.map(c => ({
        ...c,
        label: `${c.text}__${c.value}`
      }))
    }
  },
  data() {
    let piecesInCarton = 1;
    let realPiecesInCarton = 1;

    if (this.$route.path.indexOf('/produkty-indent') > -1) {
      piecesInCarton = 144;
      realPiecesInCarton = 150;
    }

    return {
      name: '',
      code: '',
      site_url: '',
      dropbox_url: '',
      price_code: '',
      disable_for_customer: false,
      disable_for_customer_api: false,
      auto_sample_date: false,
      include_discounts: true,
      include_in_services: true,
      available_in_purchase: false,
      intro: '',
      minimum_quantity: 0,
      pieces_in_carton: piecesInCarton,
      real_pieces_in_carton: realPiecesInCarton,
      description: '',
      site_description: '',
      description_for_customer: '',
      image: '',
      visualization_template: '',
      additionalCategories: {},
      category_id: null,
      loadError: false,
      loadedServices: false,
      loadedProducts: false,
      loadedCurrencies: false,
      availableServices: [],
      availableProducts: [],
      default_services: null,
      currencies: [],
      prices: [],
      price_message: '',
      cn_code: '',
      color_id: null,
      strand_id: null,
      sizes: [],
      procedures: [],
      productProperties: null,
      weight: '0.00',
      // indent products
      type: null,
      base_product_id: null,
      origin_country: 'CN',
      // internals
      categoriesList: [],
      loadedCategories: false,
      loadedProductProperties: false,
      // shippers
      purchase_customer_id: null,
      purchase_sub_customer_id: null
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/services/all-items',
        loadedKey: 'loadedServices',
        outputKey: 'availableServices',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/categories/items',
        loadedKey: 'loadedCategories',
        outputKey: 'categoriesList',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/sewing-room/items',
        loadedKey: 'loadedProductProperties',
        outputKey: 'productProperties',
        errorKey: 'loadError'
      });

      if (this.isIndentList) {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: '/products/all-items',
          loadedKey: 'loadedProducts',
          outputKey: 'availableProducts',
          errorKey: 'loadError',
          params: {
            where: {
              is_indent: 0
            }
          }
        });

        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: '/currencies/items',
          loadedKey: 'loadedCurrencies',
          outputKey: 'currencies',
          errorKey: 'loadError'
        });
      } else {
        this.loadedProducts = true;
        this.loadedCurrencies = true;
      }
    },
    setProductImage (event) {
      this.image = event.target.files[0];
    },
    setVisualizationTemplate (event) {
      this.visualization_template = event.target.files[0];
    },
    validateForm () {
      FormUtils.validate(this.$refs['product-new-form'], this.submitForm);
    },
    submitForm () {
      let imageFields = false;
      let formDataToSend = {
        is_indent: this.isIndentList ? 1 : 0,
        name: this.name,
        code: this.code,
        minimum_quantity: this.minimum_quantity,
        pieces_in_carton: this.pieces_in_carton,
        real_pieces_in_carton: this.real_pieces_in_carton,
        price_code: this.price_code,
        auto_sample_date: this.auto_sample_date,
        include_discounts: this.include_discounts,
        include_in_services: this.include_in_services,
        available_in_purchase: this.available_in_purchase,
        site_url: this.site_url,
        dropbox_url: this.dropbox_url,
        intro: this.intro,
        description: this.description,
        site_description: this.site_description,
        description_for_customer: this.description_for_customer,
        default_services: this.default_services,
        type: this.type === null ? '' : this.type,
        disable_for_customer: this.disable_for_customer,
        disable_for_customer_api: this.disable_for_customer_api,
        category_id: this.category_id,
        categories: Object.keys(this.additionalCategories).filter(key => this.additionalCategories[key] && key !== this.category_id),
        price_message: this.price_message,
        cn_code: this.cn_code,
        weight: Number(this.weight).toFixed(2)
      };

      if (this.available_in_purchase && !this.isIndentList) {
        formDataToSend.purchase_customer_id = this.$refs['purchase-shipper'].customer_id;
        formDataToSend.purchase_sub_customer_id = this.$refs['purchase-subshipper'].customer_id;
        formDataToSend.color_id = this.color_id;
        formDataToSend.strand_id = this.strand_id;
        formDataToSend.sizes = this.sizes;
        formDataToSend.procedures = this.procedures;
      } else {
        formDataToSend.purchase_customer_id = null;
        formDataToSend.purchase_sub_customer_id = null;
      }

      if (this.image) {
        formDataToSend.image = this.image;
        imageFields = ['image'];
      }

      if (this.visualization_template) {
        formDataToSend.visualization_template = this.visualization_template;

        if (imageFields) {
          imageFields.push('visualization_template');
        } else {
          imageFields = ['visualization_template'];
        }
      }

      if (this.isIndentList) {
        formDataToSend.base_product_id = this.base_product_id;
        formDataToSend.origin_country = this.origin_country;
        formDataToSend.prices = this.prices;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/products/store',
        formData: formDataToSend,
        successTitle: this.$t('product.addProductSuccessTitle'),
        successText: this.$t('product.addProductSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('product.addProductErrorText'),
        successAction: (responseData) => {
          if (this.isIndentList) {
            this.$router.push('/produkty-indent/edycja/' + responseData.id);
          } else {
            this.$router.push('/produkty/edycja/' + responseData.id);
          }
        }
      }, imageFields);
    },
    filterByForServices (option, label, search) {
      return (option.code + ' ' + option.group_name + ' ' + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    filterByForProducts (option, label, search) {
      return (option.code + ' ' + option.price_code + ' ' + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    addPrice () {
      this.prices.push({
        currency_id: null,
        price: ''
      });
    },
    removePrice (index) {
      this.prices.splice(index, 1);
    },
    setIndentProductType (type) {
      this.type = type;

      if (type === 'CAP') {
        this.weight = '0.08';
        this.cn_code = '65050030';
      } else if (type === 'WINTER') {
        this.weight = '0.09';
        this.cn_code = '65050090';
      }
    },
    getMaterialNameByColor (colorID) {
      if (this.productProperties && this.productProperties.materials) {
        for (let i = 0; i < this.productProperties.materials.length; i++) {
          let material = this.productProperties.materials[i];
          let colors = material.colors;

          for (let j = 0; j < colors.length; j++) {
            if (colors[j].id === colorID) {
              return material.name;
            }
          }
        }
      }

      return '--';
    }
  }
};
</script>

<style lang="scss" scoped>
.additional-categories {
  list-style-type: none;
  padding-left: 0;

  .additional-category.is-disabled {
    opacity: 0.5;

    /deep/ .custom-toggle .custom-control-input ~ .custom-control-label::before {
      background: #17c671;
      border-color: #17c671;
    }

    /deep/ .custom-toggle.custom-toggle-sm .custom-control-input ~ .custom-control-label::after {
      background: #fff;
      left: 1.9375rem;
      transform: translateX(-100%);
    }
  }
}
</style>
